import {Link, useParams} from "react-router-dom";
import {useAlert, useAuth} from "../../../context";
import {useOrder, useOrderDetails} from "./useOrder";
import {AdminUserAlert, ItemLoading, OurClearAlert, PageTitle} from "../../_components";
import React from "react";
import {Alert, Badge, Button, Stack, Table} from "react-bootstrap";
import {isoTimestampToReadableDate} from "../admin/utilities/readableDate";
import {centsToString} from "../admin/utilities/formatCurrency";
import {scrollToTop} from "../../../utils2";
import {TableLikeText} from "../../_components";
import {OrderProgress} from "./OrderProgress";
import {useBrowserTitle} from "../../../hooks/useBrowserTitle";

export function OrderView() {
    useBrowserTitle("Order");
    const {uid, oid} = useParams()
    const {admin} = useAuth()
    const {error} = useAlert()
    const {value: valueInvoice, getInvoice, errorValue: errorInvoice, clearErrorValue: clearErrorInvoice} = useOrder(oid)

    const {
        value,
        loadingValue,
        errorValue,
        clearErrorValue,
    } = useOrderDetails(oid)

    function handleDownload() {
        getInvoice(valueInvoice.id)
            .then(() => {
                scrollToTop()
            })
            .catch((err) => {
                error("Cannot download the PDF")
                console.error('Error downloading the PDF:', err)
            })
    }

    const capitalizeString = str => str.charAt(0).toUpperCase() + str.slice(1)


    return <>
        <AdminUserAlert/>
        <OurClearAlert variant="danger" msg={errorValue} onClear={clearErrorValue}/>
        <OurClearAlert variant="danger" msg={errorInvoice} onClear={clearErrorInvoice}/>

        {value.payment_method.toLowerCase() === "bank" && value.status === "submitted" &&
            <Alert variant="info">
                <div className="d-flex flex-column">
                    <div className="fw-semibold"><i className="bi bi-info-circle me-2"/>Payment Missing</div>
                    To proceed with the shipment, please complete the bank transfer!
                </div>
            </Alert>
        }

        <OrderProgress currentStep={value.status}/>

        <PageTitle title="Orders Details"></PageTitle>

        {value && !loadingValue ? <>
            <div className="d-flex flex-wrap justify-content-between my-2">
                <RenderAddress title="Delivery address" value={value} type="shipping"/>
                <RenderAddress title="Billing address" value={value} type="billing"/>
            </div>

            <div className="my-2">
                <TableLikeText title="Orders number" value={value.order_number}/>
                <TableLikeText title="Orders placed on" value={isoTimestampToReadableDate(value.created_at)}/>
                <TableLikeText title="Payment method" value={capitalizeString(value.payment_method)}/>
                {value.shipped_at && <TableLikeText title="Shipped at" value={isoTimestampToReadableDate(value.shipped_at)}/>}

            </div>

            <div className="my-2">
                <Table className="">
                    <thead className="">
                    <tr>
                        <th>Article</th>
                        <th className="text-end">Qty.</th>
                        <th className="text-end">Price (excl. VAT)</th>
                        <th className="text-end">Subtotal</th>
                    </tr>
                    </thead>
                    <tbody className="table-group-divider">
                    {Array.isArray(value?.order_articles) && value.order_articles.map((item, index) => (
                        <tr key={index}>
                            <td>
                                <div>
                                    <div>{item.title}</div>
                                    <Link to={`/shop/detail/${item.product_id}`}
                                          className="flex-shrink-0 link-body-emphasis link-underline link-underline-opacity-0 link-underline-opacity-75-hover link-offset-1 link-offset-1-hover">
                                        <span className="text-body-secondary">SKU: {item.SKU}</span>
                                    </Link>
                                </div>
                            </td>
                            <td className="text-end">{item.quantity}</td>
                            <td className="text-end">€ {centsToString(item.unit_price_in_cents)}</td>
                            <td className="text-end">€ {centsToString(item.total_price_in_cents)}</td>
                        </tr>

                    ))}
                    <tr>
                        {/*<td colSpan={3} className="">{value.shipping_company} {value.shipping_service}</td>*/}
                        <td colSpan={2} className="">
                            <div>
                                <div>{value.shipping_company}</div>
                                <div className="text-body-secondary">{value.shipping_service}</div>
                            </div>
                        </td>
                        <td className="text-end">€ {centsToString(value.shipping_price_in_cents)}</td>
                        <td className="text-end">€ {centsToString(value.shipping_price_in_cents)}</td>
                    </tr>
                    {value?.discount_in_cents > 0 && <tr>
                        <td colSpan={2} className="">Discount</td>
                        <td></td>
                        <td className="text-end">- € {centsToString(value?.discount_in_cents)}</td>
                    </tr>}

                    {value.vat_percentage > 0 && <tr>
                        <td colSpan={3} className="">VAT {value.vat_percentage}%</td>
                        <td className="text-end">€ {centsToString(value.total_gross_in_cents - value.total_net_in_cents)}</td>
                    </tr>}

                    </tbody>
                    <tfoot className="table-group-divider">
                    <tr className="fw-bolder">
                        <td colSpan={3} className="text-end">Total</td>
                        <td className="text-end">€ {centsToString(value.total_gross_in_cents)}</td>
                    </tr>
                    </tfoot>
                </Table>
            </div>

            <PaymentDetails status={value.status} method={value.payment_method} payment={value.payment_details} orderNumber={value.order_number}/>

            <Stack direction="horizontal" gap="2" className="py-3 justify-content-end">
                <Button as={Link} variant="link" to={-1}>Go back</Button>
                {value.payment_method === "Bank" && value.status === "submitted" ? <Button variant="warning" onClick={handleDownload}>Proforma Invoice</Button> :
                    <Button variant="warning" onClick={handleDownload}>Invoice</Button>}
                {admin && <Button variant="primary" as={Link} to={`/backend/dispatch/${uid}/${oid}`}>Edit</Button>}
            </Stack>
        </> : (
            <ItemLoading show={loadingValue}/>
        )}
    </>
}

function RenderAddress({title, value, type}) {
    const nameKey = `${type}_name`
    const lineKey = `${type}_line1`
    const line2Key = `${type}_line2`
    const cityKey = `${type}_city`
    const zipKey = `${type}_zip`
    const countryKey = `${type}_country`
    const phoneKey = `${type}_phone`

    return <div>
        <div className="text-body-secondary">{title}:</div>
        <div className="">
            {value && (
                <>
                    <div className="fw-medium">{`${value[nameKey]}`}</div>
                    <div>{`${value[lineKey]}`}</div>
                    {value[line2Key] && <div>{`${value[line2Key]}`}</div>}
                    <div>{`${value[cityKey]} ${value[zipKey]}, ${value[countryKey]}`}</div>
                    <div>{`+${value[phoneKey]}`}</div>
                </>
            )}
        </div>
    </div>
}


function PaymentDetails({status, method, payment, orderNumber}) {

    try {
        if (!payment)
            return null

        const payment_details = JSON.parse(payment)

        return <>
            <div className="d-flex align-items-center">
                <span className="fw-semibold fs-4">Payment details</span>
                {status === "submitted" && <span className="ms-2"><Badge bg="danger">Unpaid</Badge></span>}
                {(status === "paid" || status === "shipping" || status === "delivered") && <span className="ms-2"><Badge bg="info">Approved</Badge></span>}
            </div>
            {method.toLowerCase() === "bank" &&
                <>
                    <TableLikeText title="Account Name" value={payment_details?.account}/>
                    <TableLikeText title="Bank Name" value={payment_details?.bank}/>
                    <TableLikeText title="IBAN" value={payment_details.IBAN}/>
                    <TableLikeText title="SWIFT/BIC" value={payment_details?.SWIFT}/>
                    <TableLikeText title="Reference" value={orderNumber}/>
                </>}

            {method.toLowerCase() === "paypal" &&
                <>
                    <TableLikeText title="Transaction ID" value={payment_details?.transaction_id}/>
                </>
            }

            {method.toLowerCase() === "bitcoin" &&
                <>
                    <TableLikeText title="Transaction ID" value={payment_details?.transaction_id}/>
                </>
            }
        </>
    } catch {
        return <Alert variant="danger">Failed to get payment details, retry later.</Alert>
    }
}